import { useState, useEffect } from "react";
import { Copy, CheckCircle, XCircle, BookOpenText, TriangleAlert } from "lucide-react";
import { API_PROD_URL } from "src/utils/axios";
import googleIcon from "../../assets/images/google.png";
import linkedinIcon from "../../assets/images/LinkedIn.svg";
import hubspotIcon from "../../assets/images/hubspot.png";
import apolloIcon from "../../assets/images/apollo.png";
import airtableIcon from "../../assets/images/airtable.png"

interface Props {
  campaignData: any;
  isOpen?: any;
  onConnected?: () => void;
}

const ConnectorSetup = ({ campaignData, isOpen, onConnected }: Props) => {
  const [connectors, setConnectors] = useState([
    { id: 1, name: "HubSpot CRM", icon: hubspotIcon, status: "Disconnected", isConnected: false, desc: "Sync → Daily midnight + on-change webhook" },
    { id: 2, name: "LinkedIn Ads", icon: linkedinIcon, status: "Disconnected", isConnected: false, desc: "Sync → Daily 6am PST + spend alerts" },
    { id: 3, name: "Apollo", icon: apolloIcon, status: "Disconnected", isConnected: false, desc: "Sync → Daily + webhook triggers" },
  ]);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupRef, setPopupRef] = useState<Window | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showWarning, setShowWarning] = useState(true);

  // Toast function
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Function to check HubSpot connection status
  const checkHubSpotStatus = async (userId: string) => {
    try {
      const response = await fetch(`${API_PROD_URL}/hubspot/oauth/status?user_id=${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        const isConnected = data.connected || data.isConnected || false;
        
        setConnectors((prev) =>
          prev.map((c) =>
            c.name === "HubSpot CRM"
              ? { 
                  ...c, 
                  status: isConnected ? "Connected" : "Disconnected", 
                  isConnected: isConnected 
                }
              : c
          )
        );
        
        return isConnected;
      } else {
        console.error("Failed to check HubSpot status:", response.statusText);
        return false;
      }
    } catch (error) {
      console.error("Failed to check HubSpot status:", error);
      return false;
    }
  };

  // Function to check LinkedIn connection status
  const checkLinkedInStatus = async (userId: string) => {
    try {
      const response = await fetch(`${API_PROD_URL}/linkedin/status?user_id=${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        const isConnected = data.status;

        setConnectors((prev) =>
          prev.map((c) =>
            c.name === "LinkedIn Ads"
              ? { 
                  ...c, 
                  status: isConnected ? "Connected" : "Disconnected", 
                  isConnected: isConnected 
                }
              : c
          )
        );
        
        return isConnected;
      } else {
        console.error("Failed to check LinkedIn status:", response.statusText);
        return false;
      }
    } catch (error) {
      console.error("Failed to check LinkedIn status:", error);
      return false;
    }
  };

  // Function to disconnect HubSpot
  const disconnectHubSpot = async (userId: string) => {
    try {
      const response = await fetch(`${API_PROD_URL}/hubspot/oauth/disconnect?user_id=${userId}`, {
        method: 'GET',
      });
      
      if (response.ok) {
        setConnectors((prev) =>
          prev.map((c) =>
            c.name === "HubSpot CRM"
              ? { ...c, status: "Disconnected", isConnected: false }
              : c
          )
        );
        showToast("HubSpot disconnected successfully!", "success");
        
        window.dispatchEvent(new CustomEvent('hubspot-status-changed', { 
          detail: { isConnected: false } 
        }));
      } else {
        throw new Error('Failed to disconnect');
      }
    } catch (error) {
      console.error("Failed to disconnect HubSpot:", error);
      showToast("Failed to disconnect HubSpot. Please try again.", "error");
    }
  };

  // Function to disconnect LinkedIn
  const disconnectLinkedIn = async (userId: string) => {
    try {
      const response = await fetch(`${API_PROD_URL}/linkedin/disconnect?user_id=${userId}`, {
        method: 'GET',
      });
      
      if (response.ok) {
        setConnectors((prev) =>
          prev.map((c) =>
            c.name === "LinkedIn Ads"
              ? { ...c, status: "Disconnected", isConnected: false }
              : c
          )
        );
        showToast("LinkedIn disconnected successfully!", "success");
        
        window.dispatchEvent(new CustomEvent('linkedin-status-changed', { 
          detail: { isConnected: false } 
        }));
      } else {
        throw new Error('Failed to disconnect');
      }
    } catch (error) {
      console.error("Failed to disconnect LinkedIn:", error);
      showToast("Failed to disconnect LinkedIn. Please try again.", "error");
    }
  };

  // Check status when modal opens
  useEffect(() => {
    const checkAllStatuses = async () => {
      if (campaignData || isOpen) {
        setIsLoading(true);
        await Promise.all([
          checkHubSpotStatus(campaignData),
          checkLinkedInStatus(campaignData)
        ]);
        setIsLoading(false);
      }
    };
    
    checkAllStatuses();
  }, [campaignData, isOpen]);

  // Listen for HubSpot status changes from other component instances
  useEffect(() => {
    const handleStatusChange = (event: CustomEvent) => {
      const { isConnected } = event.detail;
      setConnectors((prev) =>
        prev.map((c) =>
          c.name === "HubSpot CRM"
            ? { 
                ...c, 
                status: isConnected ? "Connected" : "Disconnected", 
                isConnected: isConnected 
              }
            : c
        )
      );
    };

    window.addEventListener('hubspot-status-changed', handleStatusChange as EventListener);
    
    return () => {
      window.removeEventListener('hubspot-status-changed', handleStatusChange as EventListener);
    };
  }, []);

  // Listen for LinkedIn status changes from other component instances
  useEffect(() => {
    const handleStatusChange = (event: CustomEvent) => {
      const { isConnected } = event.detail;
      setConnectors((prev) =>
        prev.map((c) =>
          c.name === "LinkedIn Ads"
            ? { 
                ...c, 
                status: isConnected ? "Connected" : "Disconnected", 
                isConnected: isConnected 
              }
            : c
        )
      );
    };

    window.addEventListener('linkedin-status-changed', handleStatusChange as EventListener);
    
    return () => {
      window.removeEventListener('linkedin-status-changed', handleStatusChange as EventListener);
    };
  }, []);

  // Handle postMessage from popup window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      
      const { status, data } = event.data;

      if (status === "success") {
        let connectorName = "";
        let eventName = "";
        
        if (data?.includes("hubspot")) {
          connectorName = "hubspot";
          eventName = "hubspot-status-changed";
        } else if (data?.includes("linkedin")) {
          connectorName = "linkedin";
          eventName = "linkedin-status-changed";
        }

        if (connectorName) {
          setConnectors((prev) =>
            prev.map((c) =>
              c.name.toLowerCase().includes(connectorName)
                ? { ...c, status: "Connected", isConnected: true }
                : c
            )
          );
      
          showToast(`${connectorName.charAt(0).toUpperCase() + connectorName.slice(1)} connected successfully!`, "success");
          
          setIsPopupOpen(false);
          if (popupRef && !popupRef.closed) {
            popupRef.close();
          }
          
          window.dispatchEvent(new CustomEvent(eventName, { 
            detail: { isConnected: true } 
          }));

          setTimeout(() => {
            onConnected?.();
          }, 1500);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [popupRef, onConnected]);

  // Poll popup status and check connection when it closes
  useEffect(() => {
    if (!popupRef || !isPopupOpen) return;

    const pollTimer = setInterval(async () => {
      if (popupRef.closed) {
        clearInterval(pollTimer);
        setIsPopupOpen(false);
        
        // Check both statuses when popup closes
        const [hubspotConnected, linkedinConnected] = await Promise.all([
          checkHubSpotStatus(campaignData),
          checkLinkedInStatus(campaignData)
        ]);
        
        if (hubspotConnected) {
          showToast("HubSpot connected successfully!", "success");
          window.dispatchEvent(new CustomEvent('hubspot-status-changed', { 
            detail: { isConnected: true } 
          }));
          setTimeout(() => onConnected?.(), 1500);
        }
        
        if (linkedinConnected) {
          showToast("LinkedIn connected successfully!", "success");
          window.dispatchEvent(new CustomEvent('linkedin-status-changed', { 
            detail: { isConnected: true } 
          }));
          setTimeout(() => onConnected?.(), 1500);
        }
      }
    }, 500);

    return () => clearInterval(pollTimer);
  }, [popupRef, isPopupOpen, campaignData, onConnected]);

  const handleConnect = (connector: any) => {
    if (connector.name.includes("HubSpot")) {
      const statePayload = {
        user_id: campaignData,
        data: "hubspot-sync",
        redirect_url:
          "https://develop.app.perceivenow.ai/hubspot/oauth/success?status=success&data=hubspot-crm",
      };

      const backendUrl = `${API_PROD_URL}/hubspot/oauth/login?state=${encodeURIComponent(
        JSON.stringify(statePayload)
      )}`;

      const popup = window.open(
        backendUrl,
        "HubSpot Connect",
        "width=800,height=600,left=200,top=200"
      );

      if (popup) {
        setPopupRef(popup);
        setIsPopupOpen(true);
      } else {
        showToast("Please allow popups to connect HubSpot", "error");
      }
    }
    else if (connector.name.includes("LinkedIn")) {
      const redirect_url = "http://localhost:3000/linkedin/oauth/success";
      const state = `${campaignData}-${Date.now()}-${redirect_url}`;
      const backendUrl = `${API_PROD_URL}/linkedin/auth/linkedin/initiate`;
      const data = {
        "state": state,
      };
      
      fetch(backendUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      .then(response => response.json())
      .then(data => {
        if (data.auth_url) {
          const popup = window.open(
            data.auth_url,
            "LinkedIn Connect",
            "width=800,height=600,left=200,top=200"
          );

          if (popup) {
            setPopupRef(popup);
            setIsPopupOpen(true);
          } else {
            showToast("Please allow popups to connect LinkedIn", "error");
          }
        } else {
          showToast("Failed to initiate LinkedIn connection", "error");
        }
      })
      .catch(error => {
        console.error("Error initiating LinkedIn connection:", error);
        showToast("Error initiating LinkedIn connection", "error");
      });
    }
    else {
      setConnectors((prev) =>
        prev.map((c) =>
          c.id === connector.id ? { ...c, status: "Connected", isConnected: true } : c
        )
      );
    }
  };

  const handleDisconnect = (connector: any) => {
    if (connector.name === "HubSpot CRM" && campaignData) {
      disconnectHubSpot(campaignData);
    } else if (connector.name === "LinkedIn Ads" && campaignData) {
      disconnectLinkedIn(campaignData);
    } else {
      setConnectors((prev) =>
        prev.map((c) =>
          c.id === connector.id
            ? { ...c, status: "Disconnected", isConnected: false }
            : c
        )
      );
    }
  };

  useEffect(() => {
    if (!connectors.find((c) => c.name === "HubSpot CRM")?.isConnected) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  }, [connectors]);

  const handleWarningClose = () => {
    setShowWarning(false);
  };

  // Show loading state while checking status
  if (isLoading) {
    return (
      <div className="max-w-lg mx-auto bg-white rounded-lg border border-gray-300 shadow-lg overflow-hidden">
        <div className="px-2 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded flex items-center justify-center">
                <BookOpenText/>
              </div>
              <h2 className="text-md font-semibold text-gray-800">Connector Setup</h2>
            </div>
          </div>
        </div>
        <div className="px-2 pb-6 text-center">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-200 rounded-md mb-2"></div>
            <div className="h-12 bg-gray-200 rounded-md"></div>
          </div>
          <p className="text-sm text-gray-500 mt-2">Checking connection status...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Warning banner */}
      {showWarning && connectors.find((c) => c.name === "HubSpot CRM" || c.name === "LinkedIn Ads")?.isConnected && (
        <div className="max-w-lg mx-auto p-3 rounded-md flex items-center justify-between shadow-lg bg-red-100 border border-red-200 mb-3 animate-pulse">
          <div className="flex items-center space-x-2">
            <TriangleAlert strokeWidth={1.25} className="w-5 h-5 text-black"/>
            <span className="text-sm text-red-800 font-medium">
              Connectors are not connected — please link your HubSpot or LinkedIn account to proceed with campaign creation.
            </span>
          </div>
          <button onClick={handleWarningClose} className="font-bold text-red-800 hover:text-red-600">
            ✕
          </button>
        </div>
      )}
    
      <div className="max-w-lg mx-auto bg-white rounded-lg border border-gray-300 shadow-lg overflow-hidden relative">
        {/* Toast Notification */}
        {toast && (
          <div className={`absolute top-4 left-4 right-4 z-50 p-3 rounded-md shadow-lg flex items-center space-x-2 ${
            toast.type === 'success' 
              ? 'bg-green-100 border border-green-200 text-green-800' 
              : 'bg-red-100 border border-red-200 text-red-800'
          }`}>
            {toast.type === 'success' ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <XCircle className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">{toast.message}</span>
          </div>
        )}

        {/* Header */}
        <div className="px-2 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded flex items-center justify-center">
                <BookOpenText/>
              </div>
              <h2 className="text-md font-semibold text-gray-800">Connector Setup</h2>
            </div>
          </div>
        </div>

        {/* Connectors List */}
        <div className="px-2 pb-6 space-y-2">
          {connectors.map((connector) => (
            <div
              key={connector.id}
              className="flex flex-col border-2 border-gray-300 rounded-md py-2 px-1"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <div className="mr-2">
                    <img className="h-4 w-4" src={connector.icon} alt={connector.name} />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mr-2">
                    {connector.name}
                  </h3>
                  <span
                    className={`px-2 py-1 rounded-md text-xs font-normal mr-1 border-0 ${
                      connector.isConnected
                        ? "bg-[#EAFFEB] text-[#08851F]"
                        : "bg-[#FFEAEA] text-[#D51114]"
                    }`}
                  >
                    {connector.status}
                  </span>
                </div>

                {/* {connector.name === "HubSpot CRM" ? ( */}
                <button
                  onClick={() =>
                    connector.isConnected
                      ? handleDisconnect(connector)
                      : handleConnect(connector)
                  }
                  className={`items-center px-1 py-1 rounded-md text-xs font-medium border transition-colors text-[#373D3F] ${
                    connector.isConnected
                      ? "bg-white border-[#FF9894] hover:bg-[#FFEAEA]"
                      : "bg-white border-primary-800 hover:bg-primary-200"
                  }`}
                >
                  {connector.isConnected ? "Disconnect" : "Connect"}
                </button>
                {/* ) : (
                  <div className="relative group inline-block">
                    <button
                      
                      className={`items-center px-1 py-1 rounded-md text-xs font-normal border transition-colors
                        ${connector.isConnected
                          ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-gray-50 text-gray-700 border-gray-300"
                        }
                        opacity-50 cursor-not-allowed
                      `}
                    >
                      {connector.isConnected ? "Disconnect" : "Connect"}
                    </button>

                    <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block 
                                    bg-black text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                      Coming soon
                    </div>
                  </div>
                )} */}
              </div>
              <p className="text-[14px] font-semibold">{connector.desc}</p>
            </div>
          ))}
        </div>

        {isPopupOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-40">
            <div className="bg-white px-4 py-2 rounded shadow-md">
              <p className="text-sm">Authenticating connector...</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ConnectorSetup;